import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';
import { useAuth } from '@/context/AuthContext';
import {
  fetchIngredients,
  fetchLocations,
  generateKardexReport,
  type Ingredient
} from '@/services/kardex.service';
import type { KardexReportData, KardexFilters, KardexEntry } from '@/types/kardex.types';
import {
  FileText,
  Search,
  Package,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  ChevronDown,
  Check
} from 'lucide-react';

interface Location {
  id: string;
  nombre: string;
}

const getMovementDescription = (type: KardexEntry['movementType']): string => {
  const descriptions: Record<string, string> = {
    purchase: 'Compra a proveedor',
    sale: 'Venta - consumo en receta',
    adjustment: 'Ajuste de inventario',
    waste: 'Merma por deterioro',
    return: 'Devolución de cliente',
    transfer_in: 'Transferencia desde otra sucursal',
    transfer_out: 'Transferencia a otra sucursal',
    transfer: 'Transferencia entre sucursales',
    merchandise: 'Movimiento de mercadería'
  };
  return descriptions[type] || 'Movimiento de stock';
};

const getMovementTypeLabel = (type: KardexEntry['movementType']): string => {
  const labels: Record<string, string> = {
    purchase: 'Compra',
    sale: 'Venta',
    adjustment: 'Ajuste',
    waste: 'Merma',
    return: 'Devolución',
    transfer_in: 'Trans. Entrada',
    transfer_out: 'Trans. Salida',
    transfer: 'Transferencia',
    merchandise: 'Mercadería'
  };
  return labels[type] || 'Movimiento';
};

const getMovementTypeColor = (type: KardexEntry['movementType']): string => {
  const colors: Record<string, string> = {
    purchase: 'text-green-600',
    sale: 'text-blue-600',
    adjustment: 'text-purple-600',
    waste: 'text-red-600',
    return: 'text-orange-600',
    transfer_in: 'text-teal-600',
    transfer_out: 'text-amber-600',
    transfer: 'text-teal-600',
    merchandise: 'text-indigo-600'
  };
  return colors[type] || 'text-gray-600';
};

export const KardexReportComponent: React.FC = () => {
  const { selectedLocation } = useAuth();

  // Hook para manejar rangos de fechas por defecto
  const {
    quickRange,
    dateFrom,
    dateTo,
    setQuickRange,
    setManualDates
  } = useDateRangeDefaults('kardex');

  const [filters, setFilters] = useState<KardexFilters>({
    ingredientId: '',
    locationId: selectedLocation?.id || 'loc-1',
    dateFrom: '',
    dateTo: ''
  });

  const [kardexData, setKardexData] = useState<KardexReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [ingredientPopoverOpen, setIngredientPopoverOpen] = useState(false);

  // Filtrar ingredientes basado en búsqueda
  const filteredIngredients = useMemo(() => {
    if (!ingredientSearch.trim()) return ingredients;
    const search = ingredientSearch.toLowerCase();
    return ingredients.filter(ing =>
      ing.name.toLowerCase().includes(search)
    );
  }, [ingredients, ingredientSearch]);

  // Obtener nombre del ingrediente seleccionado
  const selectedIngredientName = useMemo(() => {
    const ingredient = ingredients.find(ing => ing.id === filters.ingredientId);
    return ingredient?.name || '';
  }, [ingredients, filters.ingredientId]);

  // Sincronizar las fechas del hook con los filtros
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom,
      dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    if (field === 'dateFrom') {
      setManualDates(value, dateTo);
    } else {
      setManualDates(dateFrom, value);
    }
  };

  // Cargar datos iniciales (locales e insumos) desde la API
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingFilters(true);

        // Cargar locales e insumos en paralelo
        const [locationsData, ingredientsData] = await Promise.all([
          fetchLocations(),
          fetchIngredients()
        ]);

        setLocations(locationsData);
        setIngredients(ingredientsData);
        console.log('✅ Kardex: Loaded', locationsData.length, 'locations and', ingredientsData.length, 'ingredients');
      } catch (error) {
        console.error('Error loading kardex filters:', error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadInitialData();
  }, []);

  const handleGenerateReport = async () => {
    if (!filters.ingredientId || !filters.locationId || !filters.dateFrom || !filters.dateTo) {
      return;
    }

    setLoading(true);
    try {
      const data = await generateKardexReport(filters);
      setKardexData(data);
      if (!data) {
        console.warn('No se encontraron datos para el reporte');
      }
    } catch (error) {
      console.error('Error generating Kardex report:', error);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateStr: string) => {
    // Agregar T12:00:00 para evitar problemas de zona horaria
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const groupEntriesByDate = (entries: KardexEntry[]) => {
    const grouped: { [date: string]: KardexEntry[] } = {};

    entries.forEach(entry => {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      grouped[entry.date].push(entry);
    });

    return grouped;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Reporte Kardex de Insumos</h2>
        <p className="text-gray-600">Auditoría de movimientos de stock por insumo y ubicación</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Filtros de Consulta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[120px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Turno</label>
              <Select value={filters.shift} onValueChange={(value) => setFilters(prev => ({ ...prev, shift: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar turno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los turnos</SelectItem>
                  <SelectItem value="morning">Mañana</SelectItem>
                  <SelectItem value="afternoon">Tarde</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[160px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Sucursal</label>
              <Select
                value={filters.locationId}
                onValueChange={(value) => setFilters(prev => ({ ...prev, locationId: value }))}
                disabled={loadingFilters}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingFilters ? "Cargando..." : "Seleccionar sucursal"} />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[220px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Insumo</label>
              <Popover open={ingredientPopoverOpen} onOpenChange={setIngredientPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={ingredientPopoverOpen}
                    className="w-full justify-between font-normal"
                    disabled={loadingFilters}
                  >
                    {loadingFilters
                      ? "Cargando..."
                      : selectedIngredientName || "Buscar insumo..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Buscar insumo..."
                        value={ingredientSearch}
                        onChange={(e) => setIngredientSearch(e.target.value)}
                        className="pl-8"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {filteredIngredients.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No se encontraron insumos
                      </div>
                    ) : (
                      filteredIngredients.map(ingredient => (
                        <div
                          key={ingredient.id}
                          className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                            filters.ingredientId === ingredient.id ? 'bg-gray-50' : ''
                          }`}
                          onClick={() => {
                            setFilters(prev => ({ ...prev, ingredientId: ingredient.id }));
                            setIngredientSearch('');
                            setIngredientPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              filters.ingredientId === ingredient.id
                                ? 'opacity-100 text-macartur-primary'
                                : 'opacity-0'
                            }`}
                          />
                          <span className="text-sm">{ingredient.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="min-w-[180px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Rango de Fechas</label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="kardex"
              />
            </div>

            <div className="min-w-[120px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Fecha Desde</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                disabled={quickRange !== 'custom'}
                className={`w-40 ${quickRange !== 'custom' ? 'bg-gray-50' : ''}`}
              />
            </div>

            <div className="min-w-[120px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Fecha Hasta</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateChange('dateTo', e.target.value)}
                disabled={quickRange !== 'custom'}
                className={`w-40 ${quickRange !== 'custom' ? 'bg-gray-50' : ''}`}
              />
            </div>

            <div className="min-w-[100px]">
              <label className="text-sm font-medium text-gray-700 mb-2 block">&nbsp;</label>
              <Button
                onClick={handleGenerateReport}
                disabled={!filters.ingredientId || !filters.locationId || !filters.dateFrom || !filters.dateTo || loading}
                className="bg-macartur-primary hover:bg-macartur-primary/90"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {kardexData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Saldo Inicial</p>
                    <p className="text-xl font-bold">{kardexData.initialBalance} {kardexData.unit}</p>
                    {kardexData.initialBalanceDate && (
                      <p className="text-xs text-gray-400">
                        Cierre {kardexData.initialBalanceDate.split('-').reverse().join('/')}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Entradas</p>
                    <p className="text-xl font-bold text-green-600">{kardexData.totalIn} {kardexData.unit}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Salidas</p>
                    <p className="text-xl font-bold text-red-600">{kardexData.totalOut} {kardexData.unit}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-macartur-primary/10 rounded-lg">
                    <Package className="w-5 h-5 text-macartur-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Saldo Final</p>
                    <p className="text-xl font-bold">{kardexData.finalBalance} {kardexData.unit}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Package className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Stock Actual</p>
                    <p className="text-xl font-bold text-amber-600">{kardexData.currentStock} {kardexData.unit}</p>
                    <p className="text-xs text-gray-400">En tiempo real</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Report Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-macartur-primary" />
                  Kardex: {kardexData.ingredientName} <span className="text-gray-500 font-normal">(ID: {kardexData.ingredientId})</span>
                </div>
                <div className="text-sm font-normal text-gray-500">
                  {kardexData.locationName} • {formatDate(kardexData.dateFrom)} - {formatDate(kardexData.dateTo)}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {kardexData.entries.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Sin movimientos</h3>
                  <p className="text-gray-500">No se registraron movimientos para el período seleccionado</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {(() => {
                    const groupedEntries = groupEntriesByDate(kardexData.entries);
                    const sortedDates = Object.keys(groupedEntries).sort();

                    return (
                      <div className="space-y-6">
                        {sortedDates.map((date) => {
                          const dayEntries = groupedEntries[date];
                          const lastEntry = dayEntries[dayEntries.length - 1];

                          const dailyFinalBalance = lastEntry ? lastEntry.balance : 0;
                          const dailyTotalIn = dayEntries.reduce((sum, entry) => sum + (entry.inQuantity || 0), 0);
                          const dailyTotalOut = dayEntries.reduce((sum, entry) => sum + (entry.outQuantity || 0), 0);

                          return (
                            <div key={date}>
                              {/* Date Header */}
                              <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
                                <h3 className="font-semibold text-blue-900 text-lg">
                                  📅 {formatDate(date)}
                                </h3>
                              </div>

                              {/* Entries table */}
                              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden mb-4">
                                <thead>
                                  <tr className="border-b bg-gray-50">
                                    <th className="text-left p-3 font-medium text-gray-700 w-20">Tipo</th>
                                    <th className="text-left p-3 font-medium text-gray-700 w-1/3">Descripción</th>
                                    <th className="text-center p-3 font-medium text-gray-700 w-20">Entrada</th>
                                    <th className="text-center p-3 font-medium text-gray-700 w-20">Salida</th>
                                    <th className="text-center p-3 font-medium text-gray-700 w-20">Saldo</th>
                                    <th className="text-left p-3 font-medium text-gray-700 w-44">Usuario</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dayEntries.map((entry) => (
                                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                                      <td className="p-3 w-20">
                                        <Badge
                                          variant="outline"
                                          className={`${getMovementTypeColor(entry.movementType)} border-current text-xs`}
                                        >
                                          {getMovementTypeLabel(entry.movementType)}
                                        </Badge>
                                      </td>
                                      <td className="p-3 text-gray-600 w-1/3">{entry.description}</td>
                                      <td className="p-3 text-center w-20">
                                        {entry.inQuantity && (
                                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md font-medium text-sm">
                                            {entry.inQuantity}
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-3 text-center w-20">
                                        {entry.outQuantity && (
                                          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md font-medium text-sm">
                                            {entry.outQuantity}
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-3 text-center font-medium w-20">{entry.balance}</td>
                                      <td className="p-3 text-sm text-gray-700 w-44">{entry.performedBy}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {/* Daily Summary */}
                              <div className="bg-macartur-primary/5 border border-macartur-primary/20 rounded-lg p-3 mb-6">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-macartur-primary font-semibold text-sm">📊 Resumen del Día - {formatDate(date)}</span>
                                  </div>
                                  <div className="flex items-center gap-6 text-sm">
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600">Entradas:</span>
                                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-md font-bold text-sm">
                                        {dailyTotalIn} {kardexData.unit}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600">Salidas:</span>
                                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md font-bold text-sm">
                                        {dailyTotalOut} {kardexData.unit}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600">Saldo:</span>
                                      <span className="bg-macartur-primary/10 text-macartur-primary px-2 py-1 rounded-md font-bold text-sm">
                                        {dailyFinalBalance} {kardexData.unit}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// Export with the expected name
export const KardexReport = KardexReportComponent;